import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MailCheck } from "lucide-react";
import { Dialog } from "snap-design-system";
import {
  clearAllPendingInviteIds,
  getPendingInviteIds,
  hasPendingInviteIds,
  removePendingInviteId,
} from "~/helpers/invite-link.helper";
import { InviteListDialog } from "~/containers/account/configuration/InviteListDialog";
import { getUserInvite } from "~/services/gateways/user.gateway";
import { usePermissionCheck } from "~/components/router/PermissionGuard";
import { Permission } from "~/helpers/permissions.helper";

// Evento customizado para fechar o dialog de invite link de qualquer lugar
const CLOSE_EVENT = "invite-link-dialog:close";

/**
 * Dispara o fechamento do InviteLinkDialog.
 * Pode ser chamado de qualquer componente (ex: InviteListDialogContent ao aceitar/rejeitar).
 */
export function closeInviteLinkDialog() {
  document.dispatchEvent(new Event(CLOSE_EVENT));
}

/**
 * Hook que detecta invite_ids pendentes no localStorage e, se algum deles
 * pertencer ao usuário autenticado, abre o dialog de aceite. IDs que não
 * batem com o email logado (ex.: link aberto na conta errada) são removidos
 * silenciosamente — sem dialog, sem toast.
 *
 * @param isReady - true quando o usuário está autenticado e com termos aceitos.
 * @returns O JSX do dialog para ser renderizado no componente pai.
 */
export function useInviteLinkAutoOpen(isReady: boolean) {
  const [isOpen, setIsOpen] = useState(false);
  const hasTriggeredRef = useRef(false);
  const { checkPermission } = usePermissionCheck();
  const canFetchInvites = checkPermission(Permission.GET_USER_INVITE);

  // Só consulta quando o usuário está pronto, tem permissão e há ids pendentes —
  // evita um GET a cada login mesmo quando ninguém clicou em link de convite.
  const shouldFetch = isReady && canFetchInvites && hasPendingInviteIds();

  // Mesma queryKey usada por userInviteDialogQuery em useUserCRUD para que o
  // dialog reaproveite o cache em vez de disparar um segundo fetch ao abrir.
  const { data: userInvites } = useQuery({
    queryKey: ["user", "invite", "enviado", undefined],
    queryFn: () => getUserInvite("enviado"),
    enabled: shouldFetch,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (hasTriggeredRef.current) return;
    if (!isReady) return;

    const pendingIds = getPendingInviteIds();
    if (pendingIds.length === 0) return;

    // Sem permissão ou sem como confirmar match → limpa e sai silencioso.
    if (!canFetchInvites) {
      clearAllPendingInviteIds();
      hasTriggeredRef.current = true;
      return;
    }

    if (userInvites === undefined) return;

    const apiInviteIds = new Set(userInvites.map((inv) => inv.invite_id));
    const matched = pendingIds.filter((id) => apiInviteIds.has(id));
    const unmatched = pendingIds.filter((id) => !apiInviteIds.has(id));

    // IDs que não pertencem a este usuário saem do storage sem barulho —
    // cobre o caso "link do convite aberto na conta errada".
    for (const id of unmatched) removePendingInviteId(id);

    hasTriggeredRef.current = true;

    if (matched.length === 0) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [isReady, canFetchInvites, userInvites]);

  // Escuta o evento de fechamento disparado por componentes filhos
  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    document.addEventListener(CLOSE_EVENT, handleClose);
    return () => document.removeEventListener(CLOSE_EVENT, handleClose);
  }, []);

  const InviteLinkDialog = useCallback(() => {
    if (!isOpen) return null;

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && setIsOpen(false)}>
        <Dialog.Content className="max-w-4xl" aria-describedby={undefined}>
          <Dialog.Header>
            <Dialog.Icon><MailCheck /></Dialog.Icon>
            <Dialog.Title>Convites</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <InviteListDialog.Content />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog>
    );
  }, [isOpen]);

  return { InviteLinkDialog };
}
